/**
 * useBaselinePlan - what the board says each spot is worth, before you plan.
 *
 * 🔴 THE PROBLEM IT SOLVES IS A PAGE OF BLANKS. A brand new plan has a rail at
 * every slot and a number in none of them, because a rail defaults to its
 * primary's price and there is no primary yet. The bench was fixed first, since
 * $1 is a constant the solver already reserves; a starter has no constant. So
 * the whole starting eleven read blank, and PER SPOT on a fresh plan was a mode
 * you could select and then not use.
 *
 * 🔴 THE ANSWER IS A SOLVE, NOT A HEURISTIC. "Budget divided by slots" and
 * "some fraction by position" are both guesses that would sit on screen looking
 * like advice. This runs the REAL optimizer on an empty plan at PPG on the
 * whole budget, which is the same question as "what would Fill empty spots do
 * from here", and reports what it paid per slot. So the defaults are internally
 * consistent by construction: they sum to at most the budget, they respect
 * position eligibility and the position caps, and seeding every rail from them
 * produces a plan the solver can immediately satisfy.
 *
 * 🔴 PPG AND TOTAL, ALWAYS, whatever the plan is set to. This is a reference
 * point rather than a preview: a baseline that moved when you flipped the
 * objective would make every rail on the page jump for a setting that has not
 * been applied to anything yet. And it cannot read budget mode, because the
 * numbers it produces are what budget mode would constrain: a PER SPOT solve
 * against rails that came from itself is circular.
 *
 * 🔴 MEMOISED ON PRICES AND SETTINGS, NEVER ON THE PLAN. The plan changes on
 * every keystroke; the baseline must not. Its inputs are exactly the price side
 * of the fingerprint (the board, your price sheet, your PPG sheet) plus the
 * league (roster slots, rosterable positions, budget), so it re-solves when one
 * of those moves and at no other time. One solve is ~7ms.
 */

import { useCallback, useMemo } from "react";

import { optimizePlan } from "../../utils/planOptimizer";
import { poolFor, makePriceOf } from "./useOptimizeActions";
import { makePlayerSeasonPoints } from "./ppgTotals";

/**
 * Solve an empty plan and index the result by slot.
 *
 * Pure, and exported so the properties that make it usable as a default - that
 * it fits the budget and fills the roster - can be pinned without a render.
 *
 * @returns {{ [slotId: string]: number } | null} null when the solve fails,
 *          which is a real state: a $10 budget over 16 slots has no answer, and
 *          a blank rail is the honest thing to show for it.
 */
export function baselineBySlot({ rosterSlots, pool, priceOf, valueOf, budget }) {
  if (!rosterSlots || rosterSlots.length === 0) return null;
  const result = optimizePlan({
    plan: { budget, buckets: {} },
    rosterSlots,
    pool,
    priceOf,
    valueOf,
    budget,
    budgetMode: "total",
  });
  if (!result.ok) return null;
  const bySlot = {};
  result.assignments.forEach((a) => { bySlot[a.slotId] = Math.max(1, Math.round(a.price)); });
  return bySlot;
}

export default function useBaselinePlan({
  rosterSlots,
  settings,
  budget,
  dynamicPrices,
  priceSheet,
  ppgOverrides,
  getPlayerProj,
  getPlayerGames,
}) {
  const pool = useMemo(() => poolFor(settings), [settings]);

  const baseline = useMemo(() => baselineBySlot({
    rosterSlots,
    pool,
    priceOf: makePriceOf(dynamicPrices, priceSheet),
    // Season points, not the PPG rate: the rate is what the user reads and
    // points is what the solver maximises. See useOptimizeActions for why the
    // two must not be collapsed.
    valueOf: makePlayerSeasonPoints(getPlayerProj, getPlayerGames, ppgOverrides),
    budget: Number(budget) || 0,
  }), [rosterSlots, pool, dynamicPrices, priceSheet, ppgOverrides, getPlayerProj, getPlayerGames, budget]);

  /** That solve's price for a slot, or null when there is no answer for it. */
  const baselinePriceOf = useCallback(
    (slotId) => (baseline && baseline[slotId] != null ? baseline[slotId] : null),
    [baseline],
  );

  return { baseline, baselinePriceOf };
}
